<template>
	<div id="soundPlayer" class="soundPlayer" :class="darkMode ? 'darkMode' : ''">
		<div
			class="soundPlayer-popup-backdrop"
			:class="{ 'is-open': isPlayerPopupOpen }"
			@click="closePlayerPopup"
		></div>
		<div class="soundPlayer-content soundPlayer-popup-sheet" :class="{ 'is-open': isPlayerPopupOpen }">
			<div class="soundPlayer-popup-body">
				<div class="soundPlayer-popup-grabber"></div>
				<div class="soundPlayer-popup-header">
					<button
						class="soundPlayer-popup-close"
						type="button"
						aria-label="Close player"
						@click="closePlayerPopup"
					>
						<span aria-hidden="true">&times;</span>
					</button>
					<p class="soundPlayer-popup-kicker">{{ current?.mainOutcome?.selectHeader || 'Now Playing' }}</p>
				</div>
				<div class="soundPlayer-popup-cover-wrap">
					<img
						class="soundPlayer-popup-cover"
						:key="current?.identifier"
						:src="current?.mainOutcome?.imageSrc"
						alt=""
					/>
				</div>
				<div class="soundPlayer-popup-meta">
					<h2 class="soundPlayer-popup-title">{{ getSoundTitle(current) || 'Untitled podcast' }}</h2>
					<p v-if="current?.mainOutcome?.author" class="soundPlayer-popup-author">
						{{ current?.mainOutcome?.author }}
					</p>
					<p v-if="current?.mainOutcome?.publishingDate" class="soundPlayer-popup-date">
						Published: {{ formatDateEU(current?.mainOutcome?.publishingDate || '') }}
					</p>
				</div>

				<div class="soundPlayer-popup-progress-row">
					<div class="soundPlayer-popup-play-center">
						<button v-if=" isLoading " class="soundPlayer-actions-btn soundPlayer-popup-play-btn">
							<div class="spinner">
								<div class="spinner-border"></div>
							</div>
						</button>
						<button v-else-if=" isPlaying " @click="pause()" class="soundPlayer-actions-btn soundPlayer-popup-play-btn active">
							<pause-icon :size="30" />
						</button>
						<button v-else @click.prevent="play()" class="soundPlayer-actions-btn soundPlayer-popup-play-btn">
							<play-icon :size="30" />
						</button>
					</div>
					<div class="soundPlayer-popup-progress-wrap">
						<input
							type="range"
							min="0"
							max="100"
							class="soundPlayer-slider soundPlayer-popup-slider"
							:value="slider"
							@input="onSliderInput"
							@mousedown="isSeeking = true"
							@mouseup="onSeekEnd"
							@touchstart="isSeeking = true"
							@touchend="onSeekEnd"
						/>
						<div class="soundPlayer-popup-time-row">
							<span>{{ elapsedTime }}</span>
							<span>{{ formattedTime }}</span>
						</div>
					</div>
				</div>

				<div class="soundPlayer-popup-controls-row">
					<button
						@click="toggleShuffleMode"
						class="soundPlayer-actions-btn soundPlayer-popup-action-btn"
						:title="'Shuffle: ' + (shuffleMode ? 'on' : 'off')"
						:class="{ '!text-success': shuffleMode }"
					>
						<span v-if="shuffleMode"><shuffle-icon :size="20" /></span>
						<span v-else><not-shuffle-icon class="text-success" :size="20"/></span>
					</button>
					<button @click="prevSound()" class="soundPlayer-actions-btn soundPlayer-popup-action-btn">
						<prev-icon />
					</button>
					<button @click="nextSound()" class="soundPlayer-actions-btn soundPlayer-popup-action-btn">
						<next-icon />
					</button>
					<button v-if=" showSoundList " class="soundPlayer-actions-btn soundPlayer-popup-action-btn"
						@click="showSoundList = false">
						<close-icon />
					</button>
					<button v-else class="soundPlayer-actions-btn soundPlayer-popup-action-btn"
						@click="showSoundList = true">
						<list-icon />
					</button>
					<button class="soundPlayer-actions-btn soundPlayer-popup-action-btn" @click="changeSpeed">
						{{ speeds[speedIndex] }}x
					</button>
					<button @click="toggleRepeatMode" class="soundPlayer-actions-btn soundPlayer-popup-action-btn"
						:title="'Repeat: ' + repeatMode"
						:class="{ '!text-success': repeatMode !== 'none' }">
						<template v-if=" repeatMode === 'one' "><repeat-once :size="20"/></template>
						<template v-else-if=" repeatMode === 'all' "><repeat-all :size="20"/></template>
						<template v-else><repeat-all class="text-danger" :size="20"/></template>
					</button>
					<button
						class="soundPlayer-actions-btn soundPlayer-popup-action-btn soundPlayer-volume"
						:class="{ 'is-open': isVolumeOpen }"
						@click.stop="toggleVolumeBox"
					>
							<volume-icon />
							<div class="soundPlayer-volume-box" @click.stop>
								<input v-model="volume" type="range" min="0" max="100" class="soundPlayer-volume-slider"
									@input="changeVolume" />
							</div>
					</button>
				</div>
			</div>
		</div>
		<div v-if=" !showSoundList " class="soundPlayer-soundList">
			<div class="soundPlayer-soundList-content">
				<button v-for=" soundItem in filteredSounds " :key="soundItem.identifier"
					class="soundPlayer-soundList-item flex items-start w-full !p-15px"
					:class="{
						active: current?.identifier === soundItem.identifier && isPlaying,
						'is-current': current?.identifier === soundItem.identifier,
						'is-opening': openingIdentifier === soundItem.identifier
					}"
					@click="handleSoundItemClick(soundItem)"
					@mouseenter="hoveredIdentifier = soundItem.identifier" @mouseleave="hoveredIdentifier = null">
					<div class="soundPlayer-icon-wrapper relative">
						<template v-if=" current.identifier === soundItem.identifier && isPlaying ">
							<div class="soundPlayer-icon-wrapper relative">
								<pause-circle-icon v-if=" hoveredIdentifier !== soundItem.identifier " :size="20"
									class="soundPlayer-soundList-item-icon self-center mr-2" />
								<pause-circle-icon-hover v-else :size="20"
									class="soundPlayer-soundList-item-icon-hover self-center mr-2" />
							</div>
						</template>

						<play-circle-icon v-else :size="20" class="soundPlayer-soundList-item-icon self-center mr-2" />
					</div>

					<div class="w-100px md:flex items-center justify-center flex-col p-1px ml-2 hidden">
						<img class="object-contain h-90px max-h-70px w-100%" :src="soundItem.mainOutcome.imageSrc"
							alt="" />
					</div>
					<div class="ml-2 text-left">
						<p class="text-primary font-semibold">{{ soundItem.mainOutcome.selectHeader }}</p>
						<h2 class="text-sm text-dark font-600 md:text-md mb-1"
							:class="{ 'text-primary': current.identifier === soundItem.identifier && isPlaying }">
							{{ getSoundTitle(soundItem) }}
						</h2>
						<small v-if=" soundItem.mainOutcome.publishingDate "
							class="text-success text-xs fw-500 text-left mr-2">Published: {{
								formatDateEU( soundItem.mainOutcome.publishingDate ) }}</small>
						<small v-if=" soundItem.mainOutcome.author " class="text-success text-xs fw-500 text-left">Author:
							{{
								soundItem.mainOutcome.author }}</small>
					</div>
					<div class="soundPlayer-timer text-dark ml-auto">
						<span v-if=" current.identifier === soundItem.identifier && isPlaying "
							:class="{ 'text-success': current.identifier === soundItem.identifier && isPlaying }">{{
								formattedTime }}</span>
						<span v-else>{{ soundItem.duration }}</span>
					</div>
				</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, defineEmits, onBeforeUnmount, computed } from 'vue';
import PlayIcon from './icons/PlayIcon.vue';
import PauseIcon from './icons/PauseIcon.vue';
import PrevIcon from './icons/PrevIcon.vue';
import NextIcon from './icons/NextIcon.vue';
import VolumeIcon from './icons/VolumeIcon.vue';
import ListIcon from './icons/ListIcon.vue';
import CloseIcon from './icons/CloseIcon.vue';
import PlayCircleIcon from './icons/PlayCircleIcon.vue';
import PauseCircleIcon from './icons/PauseCircleIcon.vue';
import PauseCircleIconHover from './icons/PauseCircleIconHover.vue';
import RepeatOnce from './icons/RepeatOnce.vue';
import RepeatAll from './icons/RepeatAll.vue';
import ShuffleIcon from './icons/ShuffleIcon.vue';
import NotShuffleIcon from './icons/NotShuffleIcon.vue';
import { useAppStore } from '@/stores/app-store';
import { isEnabledFlag, readAssessmentMetadataFromCallout } from '@/utils/assessmentMetadata';

interface SoundItem {
  identifier: string;
  file_url: string;
  duration?: string;
  mainOutcome: {
    selectHeader: string;
    title: string;
    imageSrc: string;
    publishingDate?: string;
    author?: string;
  };
  podcastSlideshow?: {
    slideMusic?: boolean;
  };
}

const props = defineProps<{
  sounds: SoundItem[];
  darkMode?: boolean;
  draggable?: boolean;
}>();

const { currentTestyourself } = useAppStore();

const getSoundTitle = (soundItem: SoundItem | null | undefined): string => {
  if (!soundItem) {
    return '';
  }
  const assessmentMetadata = readAssessmentMetadataFromCallout(soundItem);
  if (isEnabledFlag(currentTestyourself.value) && assessmentMetadata.title) {
    return assessmentMetadata.title;
  }
  return String(soundItem.mainOutcome?.title ?? '').trim();
};



const emits = defineEmits(['activeSound']);
const showSoundList = ref(false);
const isPlayerPopupOpen = ref(false);
const isVolumeOpen = ref(false);
const index = ref(0);
const filteredSounds = computed<SoundItem[]>(() =>
  props.sounds.filter((item) => item.file_url && item.file_url.trim() !== '')
);

const current = ref<SoundItem | null>(filteredSounds.value[index.value] || null);


const isPlaying = ref(false);
const isLoading = ref(false);
const player = new Audio();
const speedIndex = ref(0);
const speeds = [1, 1.5, 2, 0.5];
const slider = ref(0);
const isSeeking = ref(false);
const volume = ref(70);
const shuffleMode = ref(false);

const shuffledIndexes = ref<number[]>([]);
const shufflePointer = ref(0);
const repeatModes = ['none', 'one', 'all'];
const repeatIndex = ref(Number(localStorage.getItem('repeatIndex')) || 0);
const repeatMode = computed(() => repeatModes[repeatIndex.value]);
const hoveredIdentifier = ref<string | null>(null);
const openingIdentifier = ref<string | null>(null);
let openingEffectTimeout: ReturnType<typeof setTimeout> | null = null;

const openPlayerPopup = () => {
  isPlayerPopupOpen.value = true;
};

const closePlayerPopup = () => {
  isPlayerPopupOpen.value = false;
  isVolumeOpen.value = false;
  if (isPlaying.value) {
    pause();
  }
};

const toggleVolumeBox = () => {
  isVolumeOpen.value = !isVolumeOpen.value;
};

const closeVolumeBox = () => {
  isVolumeOpen.value = false;
};


const createShuffleOrder = (currentIndex: number) => {
  const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0;
  const rest = Array.from({ length: filteredSounds.value.length }, (_, i) => i).filter(
    (i) => i !== safeCurrentIndex
  );
  for (let i = rest.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [rest[i], rest[j]] = [rest[j], rest[i]];
  }
  return [safeCurrentIndex, ...rest];
};

const toggleShuffleMode = () => {
  shuffleMode.value = !shuffleMode.value;

  if (shuffleMode.value) {
    shuffledIndexes.value = createShuffleOrder(index.value);
    shufflePointer.value = 0;
  } else {
    shuffledIndexes.value = [];
    shufflePointer.value = 0;
    const currentIdentifier = current.value?.identifier;
    index.value = currentIdentifier
      ? filteredSounds.value.findIndex((item) => item.identifier === currentIdentifier)
      : 0;
  }
};

const toggleRepeatMode = () => {
  repeatIndex.value = (repeatIndex.value + 1) % repeatModes.length;
  localStorage.setItem('repeatIndex', repeatIndex.value.toString());
};

const triggerOpeningEffect = (identifier: string) => {
  openingIdentifier.value = identifier;
  if (openingEffectTimeout) {
    clearTimeout(openingEffectTimeout);
  }
  openingEffectTimeout = setTimeout(() => {
    if (openingIdentifier.value === identifier) {
      openingIdentifier.value = null;
    }
  }, 560);
};

const updateSliderStyle = () => {
  const percent = slider.value + '%';
  document.documentElement.style.setProperty('--slider-progress', percent);
};

const onSliderInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  slider.value = parseFloat(target.value);
  updateSliderStyle();
  if (isPlaying.value) player.pause();
};

const onSeekEnd = () => {
  if (!player.duration || isNaN(player.duration)) return;
  player.currentTime = (slider.value / 100) * player.duration;
  updateFormattedTime();
  if (isPlaying.value) player.play();
  isSeeking.value = false;
};

const changeVolume = (event: Event) => {
  const target = event.target as HTMLInputElement;
  player.volume = parseFloat(target.value) / 100;
};

const handleDocumentClick = () => {
  closeVolumeBox();
};

const formatDateEU = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${String(date.getDate()).padStart(2, '0')}.${String(
    date.getMonth() + 1
  ).padStart(2, '0')}.${date.getFullYear()}`;
};


const play = (song?: SoundItem) => {
  if (song && song.identifier !== current.value?.identifier) {
    current.value = song;
    player.src = song.file_url;
    player.load();
    slider.value = 0;
  }

  isLoading.value = true;

  // gjithmonë cakto oncanplay para play()
  player.oncanplay = () => {
    player.play();
    isPlaying.value = true;
    isLoading.value = false;
    updateSliderStyle();
  };

  // nëse nuk e ndryshuam src, duhet ta ngarkojmë prap
  if (!song) {
    player.load();
  }

  if (song) {
    index.value = filteredSounds.value.findIndex(
      (item) => item.identifier === song.identifier
    );
    if (shuffleMode.value) {
      const queueIndex = shuffledIndexes.value.findIndex((item) => item === index.value);
      if (queueIndex >= 0) {
        shufflePointer.value = queueIndex;
      } else {
        shuffledIndexes.value = createShuffleOrder(index.value);
        shufflePointer.value = 0;
      }
    }
  }
};

const handleSoundItemClick = (soundItem: SoundItem) => {
  openPlayerPopup();

  if (current.value?.identifier !== soundItem.identifier) {
    triggerOpeningEffect(soundItem.identifier);
    play(soundItem);
    return;
  }

  if (isPlaying.value) {
    pause();
    return;
  }

  triggerOpeningEffect(soundItem.identifier);
  play();
};


const formattedTime = ref('00:00');
const elapsedTime = ref('00:00');
const padTime = (time: number) => time.toString().padStart(2, '0');
const updateFormattedTime = () => {
  if (!player.duration || isNaN(player.duration)) {
    formattedTime.value = '00:00';
    elapsedTime.value = '00:00';
    return;
  }

  const elapsedSeconds = Math.floor(player.currentTime);
  const remainingTime = Math.max(player.duration - player.currentTime, 0);
  const remainingSeconds = Math.ceil(remainingTime);
  elapsedTime.value = `${padTime(Math.floor(elapsedSeconds / 60))}:${padTime(
    elapsedSeconds % 60
  )}`;
  formattedTime.value = `${padTime(Math.floor(remainingSeconds / 60))}:${padTime(
    remainingSeconds % 60
  )}`;
};

const getMediaDuration = (url: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => resolve(audio.duration);
    audio.onerror = () => reject(new Error('Could not load media metadata'));
    audio.src = url;
  });
};

const nextSound = () => {
  if (!filteredSounds.value.length) {
    return;
  }
  if (shuffleMode.value && shuffledIndexes.value.length) {
    shufflePointer.value = (shufflePointer.value + 1) % shuffledIndexes.value.length;
    index.value = shuffledIndexes.value[shufflePointer.value];
  } else {
    index.value = (index.value + 1) % filteredSounds.value.length;
  }
  const nextItem = filteredSounds.value[index.value];
  triggerOpeningEffect(nextItem.identifier);
  openPlayerPopup();
  play(nextItem);
};

const prevSound = () => {
  if (!filteredSounds.value.length) {
    return;
  }
  if (shuffleMode.value && shuffledIndexes.value.length) {
    shufflePointer.value =
      (shufflePointer.value - 1 + shuffledIndexes.value.length) % shuffledIndexes.value.length;
    index.value = shuffledIndexes.value[shufflePointer.value];
  } else {
    index.value =
      (index.value - 1 + filteredSounds.value.length) % filteredSounds.value.length;
  }
  const prevItem = filteredSounds.value[index.value];
  triggerOpeningEffect(prevItem.identifier);
  openPlayerPopup();
  play(prevItem);
};

const pause = () => {
  player.pause();
  isPlaying.value = false;
};

const changeSpeed = () => {
  speedIndex.value = (speedIndex.value + 1) % speeds.length;
  player.playbackRate = speeds[speedIndex.value];
};

onMounted(async () => {
	if (shuffleMode.value) {
		shuffledIndexes.value = createShuffleOrder(index.value);
		shufflePointer.value = 0;
	}

  for (const soundItem of props.sounds) {
	
    try {
      const duration = await getMediaDuration(soundItem.file_url);
      soundItem.duration = `${Math.floor(duration / 60)}:${String(
        Math.floor(duration % 60)
      ).padStart(2, '0')}`;
    } catch {
      soundItem.duration = 'N/A';
    }
  }
  if (filteredSounds.value.length) {
    current.value = filteredSounds.value[index.value];
    player.src = current.value.file_url;
  }

  player.addEventListener('loadedmetadata', () => {
    updateFormattedTime();
  });

  player.addEventListener('ended', () => {
    if (repeatMode.value === 'one') {
      player.pause();
      player.currentTime = 0;
      setTimeout(() => {
        player.play();
        slider.value = 0;
        updateFormattedTime();
        updateSliderStyle();
      }, 100);
    } else if (repeatMode.value === 'all') {
      nextSound();
    } else {
      isPlaying.value = false;
    }
  });
  player.addEventListener('timeupdate', () => {
    if (player.duration && !isSeeking.value) {
      slider.value = (player.currentTime / player.duration) * 100;
      updateSliderStyle();
      updateFormattedTime();
    }
    isLoading.value = false;
  });
  player.volume = volume.value / 100;
  document.addEventListener('click', handleDocumentClick);
});

watch(
  () => props.sounds,
  () => {
    if (filteredSounds.value.length) {
      current.value = filteredSounds.value[index.value];
      player.src = current.value.file_url;
    }
  }
);

watch(
  () => current.value,
  () => {
    emits('activeSound', current.value);
  }
);
watch(shuffleMode, (value) => {
  if (!value) {
    shuffledIndexes.value = [];
    shufflePointer.value = 0;
  }
});


onBeforeUnmount(() => {
  player.pause();
  localStorage.removeItem('repeatIndex');
  document.removeEventListener('click', handleDocumentClick);
  if (openingEffectTimeout) {
    clearTimeout(openingEffectTimeout);
  }

});
</script>

<style>
@import '../css/main.css';

.spinner-border {
	border: 2px solid rgba(255, 255, 255, 0.2);
	border-left-color: #fff;
	border-radius: 50%;
	width: 30px;
	height: 30px;
	animation: spin 0.6s linear infinite;
}

@keyframes spin {
	from {
		transform: rotate(0deg);
	}

	to {
		transform: rotate(360deg);
	}
}

.soundPlayer-soundList-item-icon,
.soundPlayer-soundList-item-icon-hover {
	margin: 0 !important;
	transition: opacity 0.3s ease, transform 0.3s ease;
}

.soundPlayer-icon-wrapper:hover .soundPlayer-soundList-item-icon,
.soundPlayer-icon-wrapper:hover .soundPlayer-soundList-item-icon-hover {
	transform: scale(1.1);
	/* pak zmadhim në hover */
	opacity: 0.8;
	/* pak transparencë në hover */
}

.soundPlayer-slider {
	width: 100%;
	cursor: pointer;
}

.soundPlayer-volume-slider {
	width: 100%;
	cursor: pointer;
}

.soundPlayer-actions-btn[title*="Repeat: one"]:hover,
.soundPlayer-actions-btn[title*="Repeat: all"]:hover,
.soundPlayer-actions-btn svg.text-success:hover {
	color: white!important;
	background-color: #4caf50 !important;
	transition: background-color 0.3s ease, color 0.3s ease;
}
.soundPlayer-actions-btn:hover {
	color: white!important;
}

.soundPlayer-popup-backdrop {
	position: fixed;
	inset: 0;
	background: rgba(15, 23, 42, 0.28);
	backdrop-filter: blur(2px);
	opacity: 0;
	pointer-events: none;
	transition: opacity 260ms ease;
	z-index: 54;
}

.soundPlayer-popup-backdrop.is-open {
	opacity: 1;
	pointer-events: auto;
}

.soundPlayer-content.soundPlayer-popup-sheet {
	position: fixed;
	left: 50%;
	bottom: 0;
	width: min(100%, calc(80vw - 0.75rem));
	height: auto;
	max-height: 90dvh;
	min-height: 0;
	transform: translate(-50%, calc(100% + 14px));
	opacity: 0;
	pointer-events: none;
	padding: 0 !important;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	border-radius: 4px !important;
	background: linear-gradient(180deg, #f9fcfc 0%, #f4f8fb 100%) !important;
	transition:
		transform 430ms cubic-bezier(0.2, 0.9, 0.2, 1),
		opacity 260ms ease;
	z-index: 55;
}

.soundPlayer-content.soundPlayer-popup-sheet.is-open {
	transform: translate(-50%, -0.5rem);
	opacity: 1;
	pointer-events: auto;
}

.soundPlayer-popup-close {
	position: absolute;
	right: 0.55rem;
	top: 0.5rem;
	width: 36px;
	height: 36px;
	margin: 0 !important;
	padding: 0 !important;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: #1f3857 !important;
	z-index: 60;
	cursor: pointer;
}

.soundPlayer-popup-close svg {
	fill: currentColor !important;
}

.soundPlayer-popup-close span {
	display: block;
	font-size: 24px;
	font-weight: 700;
	line-height: 1;
	transform: translateY(-1px);
}

.soundPlayer-popup-body {
	display: flex;
	flex-direction: column;
	max-height: 90dvh;
	overflow-y: auto;
	padding: 0.95rem 0.95rem calc(env(safe-area-inset-bottom, 0px) + 1.05rem);
	gap: 1.15rem;
}

.soundPlayer-popup-grabber {
	width: 42px;
	height: 4px;
	border-radius: 999px;
	background: #cfd8e3;
	margin: 0 auto 0.1rem;
}

.soundPlayer-popup-header {
	text-align: left;
}

.soundPlayer-popup-kicker {
	margin: 0 0 0.6rem 0;
	font-size: 18px;
	font-weight: 750;
	letter-spacing: -0.01em;
	line-height: 1.12;
	color: #1b2a3f;
	max-width: 80%;
}

.soundPlayer-popup-cover-wrap {
	background: #edf2f9;
	border-radius: 12px;
	padding: 0.3rem;
	margin-top: 0.2rem;
	margin-bottom: 0.2rem;
}

.soundPlayer-popup-cover {
	display: block;
	margin: 0 auto;
	width: 100%;
	height: clamp(320px, 56vh, 520px);
	object-fit: contain;
	object-position: center;
	border-radius: 10px;
}

.soundPlayer-popup-meta {
	text-align: left;
	padding: 0 0.2rem;
	margin-top: 0.3rem;
}

.soundPlayer-popup-title {
	margin: 0;
	font-size: 18px;
	font-weight: 700;
	line-height: 1.24;
	color: #20324a;
}

.soundPlayer-popup-author {
	margin: 0.08rem 0 0;
	font-size: 14px;
	font-weight: 600;
	color: #2e6f64;
}

.soundPlayer-popup-date {
	margin: 0.04rem 0 0;
	font-size: 14px;
	font-weight: 600;
	color: #6e7d94;
}

.soundPlayer-popup-progress-row {
	display: flex;
	align-items: center;
	gap: 1.15rem;
	margin: 0.55rem 0 1.3rem;
}

.soundPlayer-popup-play-center {
	display: flex;
	justify-content: center;
	align-items: center;
	flex-shrink: 0;
}

.soundPlayer-popup-progress-wrap {
	flex: 1;
	min-width: 0;
	position: relative;
	margin-top: 0;
}

.soundPlayer-popup-slider {
	margin: 0 !important;
}

.soundPlayer-popup-time-row {
	position: absolute;
	left: 0;
	right: 0;
	top: calc(100% + 0.14rem);
	display: flex;
	justify-content: space-between;
	font-size: 0.78rem;
	font-weight: 600;
	color: #5f708b;
}

.soundPlayer-popup-controls-row {
	display: grid;
	grid-template-columns: repeat(7, minmax(0, 1fr));
	gap: 0.55rem;
	align-items: center;
	margin-top: 0.9rem;
}

#soundPlayer.soundPlayer .soundPlayer-content.soundPlayer-popup-sheet .soundPlayer-popup-play-btn {
	width: 70px !important;
	height: 70px !important;
	border-radius: 999px !important;
	background: #4d6680 !important;
	color: #fff !important;
	border: 0 !important;
	justify-self: center;
	box-shadow: none !important;
}

#soundPlayer.soundPlayer .soundPlayer-content.soundPlayer-popup-sheet .soundPlayer-popup-play-btn svg {
	fill: #fff !important;
}

#soundPlayer.soundPlayer .soundPlayer-content.soundPlayer-popup-sheet .soundPlayer-popup-play-btn:hover,
#soundPlayer.soundPlayer .soundPlayer-content.soundPlayer-popup-sheet .soundPlayer-popup-play-btn:focus-visible {
	background: #4d6680 !important;
	border-color: transparent !important;
	transform: none !important;
}

.soundPlayer-content.soundPlayer-popup-sheet .soundPlayer-popup-action-btn {
	width: 100% !important;
	height: 44px !important;
	min-width: 0 !important;
	min-height: 44px !important;
	margin: 0 !important;
	padding: 0 !important;
	display: inline-flex !important;
	align-items: center;
	justify-content: center;
	border-radius: 10px !important;
	border: 1px solid #c9d8ec !important;
	background: #f7fbff !important;
	color: #2f506d !important;
	box-shadow: 0 1px 0 rgba(255, 255, 255, 0.88) inset;
}

.soundPlayer-content.soundPlayer-popup-sheet .soundPlayer-popup-action-btn svg {
	fill: currentColor !important;
}

.soundPlayer-content.soundPlayer-popup-sheet .soundPlayer-popup-action-btn:hover {
	background: #ebf4ff !important;
	border-color: #bad0ea !important;
	color: #214969 !important;
}

.soundPlayer-content.soundPlayer-popup-sheet .soundPlayer-popup-action-btn.soundPlayer-volume {
	position: relative;
	min-width: 44px !important;
	width: 100% !important;
	height: 44px !important;
}

.soundPlayer-content.soundPlayer-popup-sheet .soundPlayer-popup-action-btn.soundPlayer-volume .soundPlayer-volume-box {
	opacity: 0;
	visibility: hidden;
	left: 50%;
	top: -86px;
	transform: translateX(-50%) rotate(-90deg);
}

.soundPlayer-content.soundPlayer-popup-sheet .soundPlayer-popup-action-btn.soundPlayer-volume.is-open .soundPlayer-volume-box,
.soundPlayer-content.soundPlayer-popup-sheet .soundPlayer-popup-action-btn.soundPlayer-volume:focus-within .soundPlayer-volume-box {
	opacity: 1;
	visibility: visible;
}

@media (max-width: 767px) {
	.soundPlayer-content.soundPlayer-popup-sheet {
		width: calc(100vw - 0.35rem);
		min-height: 0;
	}

	.soundPlayer-content.soundPlayer-popup-sheet.is-open {
		transform: translate(-50%, -0.25rem);
	}

	.soundPlayer-popup-body {
		padding: 0.72rem 0.72rem calc(env(safe-area-inset-bottom, 0px) + 0.86rem);
		gap: 0.75rem;
	}

	.soundPlayer-popup-cover {
		height: clamp(260px, 50vh, 420px);
	}

	.soundPlayer-popup-progress-row {
		gap: 0.72rem;
		margin: 0.35rem 0 1rem;
	}

	.soundPlayer-popup-controls-row {
		grid-template-columns: repeat(7, minmax(0, 1fr));
		gap: 0.36rem;
		margin-top: 0.62rem;
	}

	.soundPlayer-popup-action-btn {
		height: 40px !important;
		min-height: 40px !important;
	}

	#soundPlayer.soundPlayer .soundPlayer-content.soundPlayer-popup-sheet .soundPlayer-popup-play-btn {
		width: 62px !important;
		height: 62px !important;
	}
}
</style>
